import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import CandidateAuthModal from './components/CandidateAuthModal';
import candidateAuthService from '../../services/candidateAuthService';

const DirectJobApply = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [job, setJob] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [candidate, setCandidate] = React.useState(candidateAuthService.getCurrentCandidate());
    const [showAuthModal, setShowAuthModal] = React.useState(false);

    // Candidate Form State
    const [formData, setFormData] = React.useState({
        name: candidate?.full_name || candidate?.name || '',
        phone: candidate?.phone || '',
        email: candidate?.email || '',
        skills: '',
        experience_years: 0,
        skill_level: 'entry',
        source: 'job_marketplace',
        notes: '',
        current_salary: '',
        expected_salary: '',
        notice_period: '',
        location_preferred: '',
        previous_company: '',
        previous_company_details: '',
        resume_url: ''
    });

    const [resumeFile, setResumeFile] = React.useState(null);
    const [resumePreview, setResumePreview] = React.useState(null);
    const [uploading, setUploading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState(null);
    const [submittedSuccess, setSubmittedSuccess] = React.useState(false);
    const [copiedLink, setCopiedLink] = React.useState(false);

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
    const directJobUrl = `${window.location.origin}/apply/${jobId}`;

    React.useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(`${API_URL}/marketplace-jobs`);
                if (response.data.success) {
                    const allJobs = response.data.data;
                    const found = allJobs.find(j => String(j.id) === String(jobId));
                    if (found) {
                        setJob({
                            ...found,
                            salaryMin: parseInt(found.salary_min) || 0,
                            salaryMax: parseInt(found.salary_max) || 0,
                            postedDate: found.posted_at
                        });
                    } else {
                        setError('Job position not found or has been closed.');
                    }
                } else {
                    setError('Failed to load job details.');
                }
            } catch (err) {
                console.error('Error fetching job:', err);
                setError('Failed to connect to job marketplace server.');
            } finally {
                setLoading(false);
            }
        };

        if (jobId) {
            fetchJobDetails();
        }

        const unsub = candidateAuthService.onAuthStateChange(({ user }) => {
            setCandidate(user);
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    name: prev.name || user.full_name || user.name || '',
                    email: prev.email || user.email || '',
                    phone: prev.phone || user.phone || ''
                }));
            }
        });
        return () => unsub.unsubscribe();
    }, [jobId, API_URL]);

    const formatSalary = (min, max) => {
        if (!min && !max) return 'Best in Industry';
        return `₹${(min / 100000)?.toFixed(1)}L - ₹${(max / 100000)?.toFixed(1)}L per annum`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setSubmitError('Please upload a PDF or Word document');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setSubmitError('File size must be less than 5MB');
                return;
            }
            setResumeFile(file);
            setResumePreview(file.name);
            setSubmitError(null);
        }
    };

    const uploadResume = async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('type', 'resume');

        let res = null;
        try {
            res = await axios.post(`${API_URL}/marketplace-upload`, formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } catch (e1) {
            try {
                res = await axios.post(`${API_URL}/upload.php`, formDataUpload, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } catch (e2) {
                res = await axios.post(`${API_URL}/upload`, formDataUpload, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
        }

        const url = res?.data?.data?.url || res?.data?.url || res?.data?.data?.relative_url || res?.data?.relative_url;
        if (!url) {
            throw new Error(res?.data?.message || res?.data?.error || 'Failed to upload resume file');
        }
        return url;
    };

    const handleCopyShareLink = () => {
        navigator.clipboard.writeText(directJobUrl);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 3000);
    };

    const handleDownloadQR = () => {
        const svg = document.getElementById('job-qr-code')?.querySelector('svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new window.Image();
        img.onload = () => {
            canvas.width = img.width + 40;
            canvas.height = img.height + 40;
            if (ctx) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 20, 20);
                const pngFile = canvas.toDataURL('image/png');
                const downloadLink = document.createElement('a');
                downloadLink.download = `${job?.title || 'Job'}-Apply-QR.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            }
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);

        // Validation
        if (!formData.name.trim()) {
            setSubmitError('Full name is required');
            setSubmitting(false);
            return;
        }
        if (!formData.phone.trim()) {
            setSubmitError('Phone number is required');
            setSubmitting(false);
            return;
        }

        try {
            let uploadedResumeUrl = formData.resume_url;

            if (resumeFile) {
                setUploading(true);
                try {
                    uploadedResumeUrl = await uploadResume(resumeFile);
                    setFormData(prev => ({ ...prev, resume_url: uploadedResumeUrl }));
                } catch (uploadErr) {
                    console.error('Resume upload error:', uploadErr);
                    setSubmitError(uploadErr.message || 'Failed to upload resume. Please try again.');
                    setSubmitting(false);
                    setUploading(false);
                    return;
                } finally {
                    setUploading(false);
                }
            }

            const headers = candidateAuthService.getAuthHeaders();
            const response = await axios.post(`${API_URL}/marketplace-apply`, {
                ...formData,
                candidate_id: candidate?.id || undefined,
                resume_url: uploadedResumeUrl,
                job_id: jobId,
                skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : []
            }, { headers });

            if (response.data.success) {
                const applicationId = response.data.data?.application_id || response.data.application_id;
                if (applicationId) {
                    navigate(`/ai-screening/${applicationId}`);
                } else {
                    setSubmittedSuccess(true);
                }
            } else {
                setSubmitError(response.data.message || response.data.error || 'Failed to submit job application.');
            }
        } catch (err) {
            setSubmitError(err.response?.data?.message || err.response?.data?.error || 'An error occurred while submitting your application.');
            if (err.response?.status === 409 || err.response?.data?.already_applied) {
                window.dispatchEvent(new Event('authChange'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{job ? `Apply for ${job.title} - ${job.company}` : 'Apply for Job'} | InChi Solutions</title>
                <meta name="description" content={job ? `Direct job application for ${job.title} at ${job.company}` : 'Apply for position'} />
            </Helmet>

            <div className="min-h-screen bg-background flex flex-col">
                <Header />

                <main className="flex-1 pt-20 pb-16">
                    <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6">
                        <Link
                            to="/job-marketplace"
                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-6 font-medium"
                        >
                            <Icon name="ArrowLeft" size={16} />
                            Back to Job Marketplace
                        </Link>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 bg-card rounded-2xl border border-border shadow-sm">
                                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                                <p className="text-muted-foreground font-medium">Loading position details...</p>
                            </div>
                        ) : error ? (
                            <div className="p-12 text-center bg-card rounded-2xl border border-error/20 text-error shadow-sm">
                                <Icon name="AlertCircle" size={48} className="mx-auto mb-4 opacity-70" />
                                <h3 className="text-xl font-bold mb-2">Position Not Found</h3>
                                <p className="text-muted-foreground mb-6">{error}</p>
                                <Button variant="default" onClick={() => navigate('/job-marketplace')}>
                                    Browse All Jobs
                                </Button>
                            </div>
                        ) : submittedSuccess ? (
                            <div className="bg-card rounded-2xl p-8 md:p-12 border border-success/30 shadow-xl text-center max-w-2xl mx-auto animate-fade-in-up">
                                <div className="w-20 h-20 rounded-full bg-success/20 text-success flex items-center justify-center mx-auto mb-6">
                                    <Icon name="CheckCircle2" size={48} />
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Application Submitted!</h2>
                                <p className="text-muted-foreground text-base mb-6">
                                    Thank you, <span className="font-semibold text-foreground">{formData.name}</span>. Your application for <span className="font-semibold text-primary">{job.title}</span> has been received and added to our Lead Management System.
                                </p>
                                <div className="p-4 bg-muted/40 rounded-xl text-left text-sm text-muted-foreground space-y-2 mb-8 border border-border">
                                    <p><strong className="text-foreground">Position:</strong> {job.title}</p>
                                    <p><strong className="text-foreground">Company:</strong> {job.company}</p>
                                    <p><strong className="text-foreground">Applicant Email:</strong> {formData.email || 'N/A'}</p>
                                    <p><strong className="text-foreground">Applicant Phone:</strong> {formData.phone}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button variant="default" onClick={() => navigate('/job-marketplace')}>
                                        Browse More Jobs
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Left/Main Column: Job Details & Application Form */}
                                <div className="lg:col-span-2 space-y-8">
                                    {/* Job Header */}
                                    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-sm">
                                        <div className="flex flex-col sm:flex-row items-start gap-5 mb-6">
                                            <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0 border border-border">
                                                <Image
                                                    src={job.company_logo || job.companyLogo}
                                                    alt={job.company}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{job.title}</h1>
                                                <p className="text-lg text-primary font-medium mb-3">{job.company}</p>

                                                <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                                        <Icon name="Briefcase" size={14} />
                                                        {job.experience}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success/10 text-success font-medium">
                                                        <Icon name="IndianRupee" size={14} />
                                                        {formatSalary(job.salaryMin, job.salaryMax)}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-accent font-medium">
                                                        <Icon name="MapPin" size={14} />
                                                        {job.location}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-muted-foreground font-medium">
                                                        <Icon name="Clock" size={14} />
                                                        {job.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="space-y-4 pt-4 border-t border-border">
                                            <h3 className="font-semibold text-foreground text-base">About the Role</h3>
                                            <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                                                {job.description}
                                            </p>

                                            {job.skills && (
                                                <div className="pt-2">
                                                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Key Skills</h4>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {(Array.isArray(job.skills) ? job.skills : job.skills.split(',')).map((skill, idx) => (
                                                            <span key={idx} className="px-3 py-1 bg-accent/10 text-accent rounded-lg text-xs font-medium">
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Application Form */}
                                    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-sm">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pb-3 border-b border-border">
                                            <div>
                                                <h2 className="text-xl font-bold text-foreground">Apply for Position</h2>
                                                <p className="text-sm text-muted-foreground">
                                                    Complete your candidate application for {job.title}.
                                                </p>
                                            </div>
                                            {candidate ? (
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 border border-success/20 rounded-xl text-xs font-semibold text-success">
                                                    <Icon name="CheckCircle" size={14} />
                                                    <span>Verified Candidate ({candidate.full_name || candidate.email})</span>
                                                </div>
                                            ) : null}
                                        </div>

                                        {!candidate ? (
                                            <div className="p-8 text-center bg-muted/20 border-2 border-dashed border-border rounded-2xl">
                                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-primary">
                                                    <Icon name="ShieldCheck" size={32} />
                                                </div>
                                                <h3 className="text-lg font-bold text-foreground mb-2">Candidate Authentication Required</h3>
                                                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                                                    Please sign in or create an account with email verification to submit your application. This protects your data and enables duplicate-free application tracking.
                                                </p>
                                                <Button
                                                    variant="default"
                                                    size="lg"
                                                    className="font-semibold px-8"
                                                    onClick={() => setShowAuthModal(true)}
                                                >
                                                    <Icon name="LogIn" size={18} className="mr-2" />
                                                    Sign In / Register to Apply
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                {submitError && (
                                                    <div className="p-4 mb-6 bg-error/10 border border-error/20 rounded-xl text-error text-sm flex items-center gap-3">
                                                        <Icon name="AlertCircle" size={18} />
                                                        {submitError}
                                                    </div>
                                                )}

                                                <form onSubmit={handleSubmit} className="space-y-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Full Name *</label>
                                                            <input
                                                                required
                                                                type="text"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleChange}
                                                                placeholder="Enter your name"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Phone Number *</label>
                                                            <input
                                                                required
                                                                type="tel"
                                                                name="phone"
                                                                value={formData.phone}
                                                                onChange={handleChange}
                                                                placeholder="+91-9876543210"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Email Address</label>
                                                            <input
                                                                type="email"
                                                                name="email"
                                                                value={formData.email}
                                                                onChange={handleChange}
                                                                placeholder="your.email@example.com"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Experience (Years)</label>
                                                            <input
                                                                type="number"
                                                                name="experience_years"
                                                                value={formData.experience_years}
                                                                onChange={handleChange}
                                                                min="0"
                                                                placeholder="0"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Skill Level</label>
                                                            <select
                                                                name="skill_level"
                                                                value={formData.skill_level}
                                                                onChange={handleChange}
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            >
                                                                <option value="entry">Entry Level</option>
                                                                <option value="intermediate">Intermediate</option>
                                                                <option value="senior">Senior</option>
                                                                <option value="expert">Expert</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Preferred Location</label>
                                                            <input
                                                                type="text"
                                                                name="location_preferred"
                                                                value={formData.location_preferred}
                                                                onChange={handleChange}
                                                                placeholder="e.g. Bangalore, Remote"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="text-sm font-semibold text-foreground block mb-1">Skills</label>
                                                        <input
                                                            type="text"
                                                            name="skills"
                                                            value={formData.skills}
                                                            onChange={handleChange}
                                                            placeholder="e.g. Java, Automation, Selenium, SQL"
                                                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Current Salary</label>
                                                            <input
                                                                type="text"
                                                                name="current_salary"
                                                                value={formData.current_salary}
                                                                onChange={handleChange}
                                                                placeholder="e.g. ₹6,00,000"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Expected Salary</label>
                                                            <input
                                                                type="text"
                                                                name="expected_salary"
                                                                value={formData.expected_salary}
                                                                onChange={handleChange}
                                                                placeholder="e.g. ₹8,00,000"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Notice Period</label>
                                                            <select
                                                                name="notice_period"
                                                                value={formData.notice_period}
                                                                onChange={handleChange}
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            >
                                                                <option value="">Select notice period</option>
                                                                <option value="immediate">Immediate</option>
                                                                <option value="15_days">15 Days</option>
                                                                <option value="30_days">30 Days</option>
                                                                <option value="60_days">60 Days</option>
                                                                <option value="negotiable">Negotiable</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-foreground block mb-1">Previous Company</label>
                                                            <input
                                                                type="text"
                                                                name="previous_company"
                                                                value={formData.previous_company}
                                                                onChange={handleChange}
                                                                placeholder="Previous employer name"
                                                                className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="text-sm font-semibold text-foreground block mb-1">Upload Resume</label>
                                                        {resumePreview ? (
                                                            <div className="flex items-center justify-between p-3 border border-border rounded-xl bg-muted/20">
                                                                <div className="flex items-center gap-2 overflow-hidden">
                                                                    <Icon name="FileText" size={20} className="text-primary flex-shrink-0" />
                                                                    <span className="text-sm text-foreground truncate">{resumePreview}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setResumeFile(null); setResumePreview(null); }}
                                                                    className="text-error hover:text-error/80 text-sm font-medium"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary hover:bg-muted/10 transition-colors">
                                                                <Icon name="UploadCloud" size={28} className="text-muted-foreground mb-1" />
                                                                <span className="text-xs text-muted-foreground">Click to upload PDF or Word document (Max 5MB)</span>
                                                                <input
                                                                    type="file"
                                                                    className="hidden"
                                                                    accept=".pdf,.doc,.docx"
                                                                    onChange={handleFileChange}
                                                                />
                                                            </label>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="text-sm font-semibold text-foreground block mb-1">Additional Notes</label>
                                                        <textarea
                                                            name="notes"
                                                            value={formData.notes}
                                                            onChange={handleChange}
                                                            rows={3}
                                                            placeholder="Briefly describe your experience or why you are a fit for this role..."
                                                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm resize-none"
                                                        />
                                                    </div>

                                                    <Button
                                                        type="submit"
                                                        variant="default"
                                                        size="lg"
                                                        disabled={submitting || uploading}
                                                        className="w-full py-4 font-bold text-base shadow-lg"
                                                    >
                                                        {submitting || uploading ? (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                {uploading ? 'Uploading Resume...' : 'Submitting Application...'}
                                                            </div>
                                                        ) : 'Submit Application Now'}
                                                    </Button>
                                                </form>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Right Sidebar: Dedicated QR Code & Job Link */}
                                <div className="space-y-6">
                                    <div className="bg-card rounded-2xl p-6 border border-border shadow-sm text-center sticky top-24">
                                        <h3 className="font-bold text-foreground text-lg mb-1">Dedicated Job Link</h3>
                                        <p className="text-xs text-muted-foreground mb-4">Share this link directly with candidates to apply for {job.title}</p>

                                        <div className="p-4 bg-white rounded-xl shadow-inner inline-block mb-4 border border-border" id="job-qr-code">
                                            <QRCodeSVG
                                                value={directJobUrl}
                                                size={180}
                                            />
                                        </div>

                                        <div className="space-y-3">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-center gap-2"
                                                onClick={handleCopyShareLink}
                                            >
                                                <Icon name={copiedLink ? "Check" : "Copy"} size={16} />
                                                {copiedLink ? "Link Copied!" : "Copy Job Application Link"}
                                            </Button>

                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                className="w-full justify-center gap-2 text-xs"
                                                onClick={handleDownloadQR}
                                            >
                                                <Icon name="Download" size={14} />
                                                Download QR Code Image
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                <Footer />

                {showAuthModal && (
                    <CandidateAuthModal
                        isOpen={showAuthModal}
                        job={job}
                        initialMode="login"
                        onClose={() => setShowAuthModal(false)}
                        onSuccess={(candidateUser) => {
                            setShowAuthModal(false);
                            setCandidate(candidateUser);
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default DirectJobApply;
